import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  createMessage,
  deleteMessage,
  deleteImage,
  fetchImages,
  fetchMessages,
  fetchSettings,
  login,
  logout,
  updateSettings,
  updateMessage,
  uploadImage,
} from "./api";
import { Button } from "./components/ui/button";
import { Input } from "./components/ui/input";
import { Label } from "./components/ui/label";
import { Textarea } from "./components/ui/textarea";
import { Select } from "./components/ui/select";
import { Card, CardContent, CardHeader } from "./components/ui/card";
import fallback from "./default.jpg";

const emptyMessage = {
  salutation: "",
  message: "",
  greeting: "",
  imagePath: "",
};

const AdminPanel = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [messages, setMessages] = useState([]);
  const [images, setImages] = useState([]);
  const [selectedId, setSelectedId] = useState("");
  const [form, setForm] = useState(emptyMessage);
  const [loginForm, setLoginForm] = useState({ username: "", password: "" });
  const [status, setStatus] = useState("");
  const [settingsStatus, setSettingsStatus] = useState("");
  const [defaultImagePath, setDefaultImagePath] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [toast, setToast] = useState("");
  const [isCompactView, setIsCompactView] = useState(false);
  const [isDetailView, setIsDetailView] = useState(false);
  const [showDraft, setShowDraft] = useState(false);
  const toastTimer = useRef(null);

  const resolveImage = (imagePath) => {
    if (!imagePath) return fallback;
    return imagePath;
  };

  const shareUrl = useMemo(() => {
    const id = selectedId;
    if (!id) return "";
    return `${window.location.origin}/?id=${id}`;
  }, [selectedId]);

  const loadMessages = async () => {
    setLoading(true);
    setError("");
    try {
      const data = await fetchMessages();
      setMessages(data);
      setIsAuthenticated(true);
    } catch (err) {
      if (err?.response?.status === 401) {
        setIsAuthenticated(false);
      } else {
        setError("Konnte Nachrichten nicht laden. Bitte später erneut versuchen.");
      }
    } finally {
      setLoading(false);
    }
  };

  const loadSettings = async () => {
    try {
      const data = await fetchSettings();
      setDefaultImagePath(data.defaultImagePath || "");
    } catch (err) {
      // Ignore silently; settings only add UX polish.
    }
  };

  const loadImages = async () => {
    try {
      const data = await fetchImages();
      setImages(data);
    } catch (err) {
      // Ignore silently; auth handled via message load.
    }
  };

  useEffect(() => {
    loadMessages();
    loadImages();
    loadSettings();
  }, []);

  useEffect(() => {
    if (!selectedId && !form.imagePath && images.length > 0) {
      const preferred = defaultImagePath || images[0].value;
      setForm((prev) => ({ ...prev, imagePath: preferred }));
    }
  }, [images, form.imagePath, defaultImagePath, selectedId]);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(max-width: 960px)");
    const updateView = (event) => {
      setIsCompactView(event.matches);
    };
    setIsCompactView(mediaQuery.matches);
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener("change", updateView);
      return () => mediaQuery.removeEventListener("change", updateView);
    }
    mediaQuery.addListener(updateView);
    return () => mediaQuery.removeListener(updateView);
  }, []);

  useEffect(() => {
    return () => {
      if (toastTimer.current) {
        clearTimeout(toastTimer.current);
      }
    };
  }, []);

  const handleLogin = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    try {
      await login(loginForm.username, loginForm.password);
      await loadMessages();
      await loadImages();
      await loadSettings();
    } catch (err) {
      setError("Login fehlgeschlagen. Prüfe Benutzername/Passwort.");
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = async () => {
    await logout();
    setIsAuthenticated(false);
    setMessages([]);
    setSelectedId("");
    setForm(emptyMessage);
    setDefaultImagePath("");
  };

  const handleSelectMessage = (msg) => {
    setSelectedId(msg.id);
    setForm({
      salutation: msg.salutation,
      message: msg.message,
      greeting: msg.greeting,
      imagePath: msg.imagePath || "",
    });
    setStatus("");
    setShowDraft(false);
    if (isCompactView) {
      setIsDetailView(true);
    }
  };

  const handleInputChange = (event) => {
    const { name, value } = event.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    setLoading(true);
    setError("");
    setStatus("");

    try {
      if (selectedId) {
        await updateMessage(selectedId, form);
        setStatus("Nachricht aktualisiert.");
      } else {
        const created = await createMessage(form);
        setSelectedId(created.id);
        setShowDraft(false);
        setStatus("Nachricht angelegt.");
      }
      await loadMessages();
    } catch (err) {
      setError("Speichern fehlgeschlagen. Felder prüfen und erneut versuchen.");
    } finally {
      setLoading(false);
    }
  };

  const showToast = (message) => {
    setToast(message);
    if (toastTimer.current) {
      clearTimeout(toastTimer.current);
    }
    toastTimer.current = setTimeout(() => {
      setToast("");
    }, 2200);
  };

  const handleDelete = async () => {
    if (!selectedId) return;
    if (!window.confirm("Nachricht wirklich löschen?")) return;

    setLoading(true);
    setError("");
    try {
      await deleteMessage(selectedId);
      setSelectedId("");
      setForm(emptyMessage);
      await loadMessages();
      setStatus("Nachricht gelöscht.");
    } catch (err) {
      setError("Löschen fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  };

  const handleShare = async () => {
    if (!shareUrl) return;
    setStatus("");
    try {
      if (navigator.share) {
        await navigator.share({
          url: shareUrl,
          title: "Christmas Greeting",
        });
        setStatus("Link geteilt.");
      } else if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        setStatus("Link in die Zwischenablage kopiert.");
      } else {
        setStatus(`Link: ${shareUrl}`);
      }
    } catch (err) {
      setStatus("Teilen abgebrochen.");
    }
  };

  const handleCopyLink = async () => {
    if (!shareUrl) return;
    try {
      if (navigator.clipboard?.writeText) {
        await navigator.clipboard.writeText(shareUrl);
        showToast("Link kopiert.");
      } else {
        showToast(`Link: ${shareUrl}`);
      }
    } catch (err) {
      setStatus("Link konnte nicht kopiert werden.");
    }
  };

  const newMessage = () => {
    setSelectedId("");
    setForm(emptyMessage);
    setStatus("");
    setShowDraft(true);
    if (isCompactView) {
      setIsDetailView(true);
    }
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    setLoading(true);
    setError("");
    setStatus("");
    try {
      const uploaded = await uploadImage(file);
      setForm((prev) => ({ ...prev, imagePath: uploaded.value }));
      await loadImages();
      setStatus("Bild hochgeladen.");
      event.target.value = "";
    } catch (err) {
      setError("Upload fehlgeschlagen.");
    } finally {
      setLoading(false);
    }
  };

  const previewUrl = useMemo(
    () => resolveImage(form.imagePath),
    [form.imagePath]
  );

  const handleDefaultImageChange = (event) => {
    setDefaultImagePath(event.target.value);
    setSettingsStatus("");
  };

  const handleSaveDefaultImage = async () => {
    setSettingsStatus("");
    try {
      await updateSettings({ defaultImagePath });
      setSettingsStatus("Standardbild gespeichert.");
    } catch (err) {
      setSettingsStatus("Standardbild konnte nicht gespeichert werden.");
    }
  };

  const handleRemoveImageFromMessage = () => {
    setForm((prev) => ({ ...prev, imagePath: "" }));
    setStatus("Bild für diese Nachricht entfernt.");
  };

  const handleDeleteImageGlobally = async (image) => {
    if (image.type !== "uploaded") return;
    if (!window.confirm("Bild löschen und aus Nachrichten entfernen?")) return;
    setLoading(true);
    setError("");
    setStatus("");
    try {
      await deleteImage(image.value);
      await loadImages();
      await loadMessages();
      if (form.imagePath === image.value) {
        setForm((prev) => ({ ...prev, imagePath: "" }));
      }
      setStatus("Bild gelöscht und aus Nachrichten entfernt.");
    } catch (err) {
      setError("Bild konnte nicht gelöscht werden.");
    } finally {
      setLoading(false);
    }
  };

  const closeOverflowMenu = (event) => {
    const details = event.currentTarget.closest("details");
    if (details) {
      details.removeAttribute("open");
    }
  };

  if (!isAuthenticated) {
    return (
      <div className="admin-shell">
        <Card className="admin-panel">
          <CardHeader>
            <h1>Admin Login</h1>
          </CardHeader>
          <CardContent>
            <form className="admin-login" onSubmit={handleLogin}>
              <Label>
                Benutzername
                <Input
                  type="text"
                  name="username"
                  value={loginForm.username}
                  onChange={(e) =>
                    setLoginForm((prev) => ({
                      ...prev,
                      username: e.target.value,
                    }))
                  }
                  required
                />
              </Label>
              <Label>
                Passwort
                <Input
                  type="password"
                  name="password"
                  value={loginForm.password}
                  onChange={(e) =>
                    setLoginForm((prev) => ({
                      ...prev,
                      password: e.target.value,
                    }))
                  }
                  required
                />
              </Label>
              <Button type="submit" disabled={loading}>
                {loading ? "Laden..." : "Login"}
              </Button>
            </form>
            {error && <p className="admin-error">{error}</p>}
          </CardContent>
        </Card>
      </div>
    );
  }

  const showList = !isCompactView || !isDetailView;
  const showEditorPanel = !isCompactView || isDetailView;

  return (
    <div className="admin-shell">
      {toast && <div className="admin-toast">{toast}</div>}
      <header className="admin-header">
        <div>
          <h1>Greeting Admin</h1>
          <p>Nachrichten verwalten und teilen.</p>
        </div>
        <div className="admin-actions">
          <Button onClick={handleLogout} type="button" variant="ghost">
            Logout
          </Button>
        </div>
      </header>

      <div className="admin-layout">
        {showList && (
          <Card className="admin-list">
            <CardHeader className="list-header">
              <h2>Alle Nachrichten</h2>
              <div className="list-actions">
                {loading && <span className="admin-hint">Laden...</span>}
                <Button onClick={newMessage} type="button" variant="default">
                  Neue Nachricht
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="message-list">
                {showDraft && (
                  <button
                    key="draft"
                    className="message-row draft active"
                    type="button"
                    onClick={() => {
                      setSelectedId("");
                      setStatus("");
                      if (isCompactView) {
                        setIsDetailView(true);
                      }
                    }}
                  >
                    <div className="message-preview">
                      <p className="preview-salutation">
                        Entwurf{form.salutation ? `: ${form.salutation}` : ""}
                      </p>
                      <p className="preview-body">
                        {form.message || "Neue Nachricht vorbereiten."}
                      </p>
                    </div>
                  </button>
                )}
                {messages.map((msg) => (
                  <button
                    key={msg.id}
                    className={`message-row ${selectedId === msg.id ? "active" : ""}`}
                    type="button"
                    onClick={() => handleSelectMessage(msg)}
                  >
                    <div className="message-preview">
                      <p className="preview-salutation">{msg.salutation}</p>
                      <p className="preview-body">
                        {msg.message} — {msg.greeting}
                      </p>
                    </div>
                  </button>
                ))}
                {messages.length === 0 && (
                  <p className="admin-hint">Noch keine Nachrichten gespeichert.</p>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {showEditorPanel && (
          <Card className="admin-editor">
            <CardHeader className="editor-header">
              <div className="editor-title">
                {isCompactView && (
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={() => setIsDetailView(false)}
                  >
                    Zurück
                  </Button>
                )}
                <h2>{selectedId ? "Nachricht bearbeiten" : "Neue Nachricht"}</h2>
              </div>
              <div className="editor-actions">
                <Button
                  type="submit"
                  form="admin-message-form"
                  disabled={loading}
                >
                  {selectedId ? "Speichern" : "Anlegen"}
                </Button>
                <Button
                  type="button"
                  variant="ghost"
                  onClick={handleCopyLink}
                  disabled={!shareUrl}
                >
                  Link kopieren
                </Button>
                <details className="overflow-menu">
                  <summary
                    className="overflow-trigger"
                    aria-label="Weitere Aktionen"
                  >
                    ...
                  </summary>
                  <div className="overflow-panel">
                    <Button
                      type="button"
                      variant="ghost"
                      onClick={(event) => {
                        handleShare();
                        closeOverflowMenu(event);
                      }}
                      disabled={!shareUrl}
                    >
                      Teilen
                    </Button>
                    {selectedId && (
                      <Button
                        type="button"
                        variant="destructive"
                        onClick={(event) => {
                          handleDelete();
                          closeOverflowMenu(event);
                        }}
                      >
                        Löschen
                      </Button>
                    )}
                  </div>
                </details>
              </div>
            </CardHeader>
            <CardContent>
              <form
                className="admin-form"
                id="admin-message-form"
                onSubmit={handleSubmit}
              >
                <Label>
                  Anrede
                  <Input
                    type="text"
                    name="salutation"
                    value={form.salutation}
                    onChange={handleInputChange}
                    required
                  />
                </Label>
                <Label>
                  Nachricht
                  <Textarea
                    name="message"
                    value={form.message}
                    onChange={handleInputChange}
                    rows={6}
                    required
                  />
                </Label>
                <Label>
                  Grußformel
                  <Input
                    type="text"
                    name="greeting"
                    value={form.greeting}
                    onChange={handleInputChange}
                    required
                  />
                </Label>
                <div className="split">
                  <Label>
                    Bildauswahl
                    <Select
                      name="imagePath"
                      value={form.imagePath}
                      onChange={handleInputChange}
                    >
                      {images.map((option) => (
                        <option value={option.value} key={`${option.type}-${option.value}`}>
                          {option.type === "uploaded" ? `Upload: ${option.label}` : option.label}
                        </option>
                      ))}
                    </Select>
                  </Label>
                  <Label>
                    Neues Bild hochladen
                    <Input
                      type="file"
                      accept="image/*"
                      onChange={handleUpload}
                    />
                  </Label>
                </div>
                <p className="admin-hint">
                  Bilder können mehrfach genutzt werden. Der Link wird automatisch erstellt,
                  sobald eine Nachricht angelegt ist.
                </p>
                {previewUrl && (
                  <div className="image-preview">
                    <img src={previewUrl} alt="Vorschau" />
                  </div>
                )}
                <div className="image-actions">
                  <Button
                    type="button"
                    variant="ghost"
                    onClick={handleRemoveImageFromMessage}
                    disabled={!form.imagePath}
                  >
                    Bild für diese Nachricht entfernen
                  </Button>
                </div>
              </form>
              {status && <p className="admin-status">{status}</p>}
              {error && <p className="admin-error">{error}</p>}
              {shareUrl && (
                <p className="admin-hint">Öffentlicher Link: {shareUrl}</p>
              )}
            </CardContent>
          </Card>
        )}

        <Card className="admin-uploads">
          <CardHeader>
            <h3>Uploads verwalten</h3>
          </CardHeader>
          <CardContent>
            <div className="default-image">
              <Label>
                Standardbild
                <Select
                  name="defaultImagePath"
                  value={defaultImagePath}
                  onChange={handleDefaultImageChange}
                >
                  {images.map((option) => (
                    <option value={option.value} key={`default-${option.type}-${option.value}`}>
                      {option.type === "uploaded" ? `Upload: ${option.label}` : option.label}
                    </option>
                  ))}
                </Select>
              </Label>
              <Button
                type="button"
                onClick={handleSaveDefaultImage}
                disabled={loading}
              >
                Standardbild speichern
              </Button>
              {settingsStatus && (
                <p className="admin-hint">{settingsStatus}</p>
              )}
            </div>
            <div className="uploads-list">
              {images
                .filter((img) => img.type === "uploaded")
                .map((img) => (
                  <div className="upload-row" key={img.value}>
                    <span className="upload-name">{img.label}</span>
                    <div className="upload-actions">
                      <details className="overflow-menu">
                        <summary
                          className="overflow-trigger"
                          aria-label="Upload Aktionen"
                        >
                          ...
                        </summary>
                        <div className="overflow-panel">
                          <Button
                            type="button"
                            size="sm"
                            onClick={(event) => {
                              setForm((prev) => ({
                                ...prev,
                                imagePath: img.value,
                              }));
                              closeOverflowMenu(event);
                            }}
                          >
                            Für Nachricht nutzen
                          </Button>
                          <Button
                            type="button"
                            size="sm"
                            variant="destructive"
                            onClick={(event) => {
                              handleDeleteImageGlobally(img);
                              closeOverflowMenu(event);
                            }}
                          >
                            Löschen
                          </Button>
                        </div>
                      </details>
                    </div>
                  </div>
                ))}
              {images.filter((img) => img.type === "uploaded").length === 0 && (
                <p className="admin-hint">Noch keine Uploads.</p>
              )}
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminPanel;
